import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CardBase from "@/components/cards/CardBase";
import { DataSourceType } from "@/lib/dataSources/types";
import { DATA_SOURCES } from "@/lib/dataSources";
import { useDataSource } from "@/lib/useDataSource";
import { ListWidgetItem } from "@/lib/dataSources";
import { formatRelativeTime } from "@/lib/utils";

interface ListWidgetProps {
  title?: string;
  dataSource?: DataSourceType;
  items?: ListWidgetItem[]; // Optional when using dataSource
  showTitle?: boolean;
  backgroundColor?: string;
  shadowType?: "black" | "gradient" | "none" | "color";
  shadowColor?: string;
  nrOfItems?: number;
  selectedColumns?: string[]; // New prop for column selection
  columnLabels?: Record<string, string>; // New prop for custom column labels
}

// Estimated heights for dynamic calculation
const ITEM_HEIGHT = 32; // Height of each list item in pixels
const SEE_MORE_HEIGHT = 28; // Height of the "see more" button
const CONTAINER_PADDING = 24; // Padding inside the container (p-4 = 16px top + some buffer)
const MIN_CONTAINER_HEIGHT_FOR_CALC = 100; // Minimum container height before we trust the calculation
const DEFAULT_VISIBLE_ITEMS = 5; // Default number of items to show before measurement

const ListWidget: React.FC<ListWidgetProps> = ({
  title = "List",
  dataSource,
  items,
  showTitle = true,
  backgroundColor = "#ffffff",
  shadowType = "black",
  shadowColor = "#000000",
  nrOfItems,
  selectedColumns,
  columnLabels
}) => {
  const [showAll, setShowAll] = useState(false);
  const [dynamicMaxVisible, setDynamicMaxVisible] = useState<number | null>(null);
  const [hasMeasured, setHasMeasured] = useState(false);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Calculate how many items can fit in the available space
  const calculateMaxVisible = useCallback(() => {
    if (!listContainerRef.current) return null;
    
    const containerHeight = listContainerRef.current.clientHeight;
    // Only calculate if container has meaningful height (layout is complete)
    // Use a higher threshold to ensure the flex layout has fully expanded
    if (containerHeight < MIN_CONTAINER_HEIGHT_FOR_CALC) return null; // Not ready yet
    
    // Reserve space for "see more" button and padding
    const availableHeight = containerHeight - SEE_MORE_HEIGHT - CONTAINER_PADDING;
    const calculatedMax = Math.max(1, Math.floor(availableHeight / ITEM_HEIGHT));
    
    // If nrOfItems is specified, use the minimum of calculated and specified
    return nrOfItems ? Math.min(nrOfItems, calculatedMax) : calculatedMax;
  }, [nrOfItems]);

  // Update max visible on mount and resize
  useEffect(() => {
    let rafId: number;
    let timeoutId: ReturnType<typeof setTimeout>;
    
    const updateMaxVisible = () => {
      const newMax = calculateMaxVisible();
      if (newMax !== null) {
        setDynamicMaxVisible(newMax);
        setHasMeasured(true);
      }
    };

    // Use requestAnimationFrame to wait for layout to complete
    const scheduleUpdate = () => {
      rafId = requestAnimationFrame(() => {
        updateMaxVisible();
        // If still not measured, try again after a short delay
        if (!hasMeasured) {
          timeoutId = setTimeout(scheduleUpdate, 50);
        }
      });
    };

    scheduleUpdate();

    // Use ResizeObserver for responsive updates after initial measurement
    const resizeObserver = new ResizeObserver(() => {
      updateMaxVisible();
    });

    if (listContainerRef.current) {
      resizeObserver.observe(listContainerRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [calculateMaxVisible, hasMeasured]);

  // Use a reasonable default before measurement, then use calculated value
  const MAX_VISIBLE = dynamicMaxVisible ?? (nrOfItems || DEFAULT_VISIBLE_ITEMS);
  
  // Use data source if provided, otherwise use static items
  const { transformedData: sourceData, isLoading } = useDataSource(
    dataSource || "glossary"
  );
  const displayItems = dataSource ? sourceData : items || [];

  // Sort items alphabetically by name
  const sortedItems = [...displayItems].sort((a, b) => 
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  );

  const visibleItems = showAll
    ? sortedItems
    : sortedItems.slice(0, MAX_VISIBLE);
  const hasMore = sortedItems.length > MAX_VISIBLE;

  // Mapping of data sources to their corresponding pages
  const getNavigationPath = (dataSourceType: DataSourceType | undefined): string => {
    switch (dataSourceType) {
      case 'glossary':
      case 'library':
      case 'glossary-sync':
      case 'favorites':
        return '/library'; // Favorites can show all types, but default to library page
      case 'glossary-blueprints':
        return '/templates';
      case 'documents':
        return '/documents';
      case 'dialogue-lines':
      case 'strings':
        return '/text-management';
      case 'user-games':
      case 'user-companies':
        return '/home'; // These are typically shown on home page
      default:
        return '/home'; // Default fallback
    }
  };

  const handleSeeMoreClick = () => {
    if (dataSource) {
      // Navigate to the appropriate page for this data source
      const path = getNavigationPath(dataSource);
      navigate(path);
    } else {
      // If no data source, just show all items in the widget
      setShowAll(true);
    }
  };

  // Get navigation path for clicking on an individual item
  const getItemNavigationPath = (item: ListWidgetItem, dataSourceType: DataSourceType | undefined): string | null => {
    if (!item.id) return null; // Can't navigate without an ID
    
    switch (dataSourceType) {
      case 'glossary':
      case 'library':
      case 'glossary-sync':
        return `/library/${item.id}`;
      case 'glossary-blueprints':
        return `/templates/${item.id}`;
      case 'documents':
        return `/documents/${item.id}`;
      case 'favorites':
        // For favorites, determine navigation based on item_type in the data
        if (item.data?.item_type === 'library') return `/library/${item.id}`;
        if (item.data?.item_type === 'document') return `/documents/${item.id}`;
        if (item.data?.item_type === 'blueprint') return `/templates/${item.id}`;
        return null;
      default:
        return null; // No navigation for other types
    }
  };

  const handleItemClick = (item: ListWidgetItem) => {
    const path = getItemNavigationPath(item, dataSource);
    if (path) {
      navigate(path);
    }
  };

  return (
    <CardBase 
      className="flex flex-col h-full w-full flex-1 overflow-hidden" 
      shadowType={shadowType} 
      shadowColor={shadowColor} 
      backgroundColor={backgroundColor}
    >
      {showTitle && (
        <div className="p-4 pb-2 border-b border-gray-300 flex-shrink-0">
          <span className="text-lg font-semibold text-foreground">
            {title}
          </span>
        </div>
      )}
      <div
        ref={listContainerRef}
        className={`${
          showTitle ? "p-4 pt-2" : "p-4"
        } flex flex-col flex-1 w-full min-h-0 overflow-hidden`}
      >
        {isLoading && (
          <div className="flex items-center justify-center py-4 flex-1">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
          </div>
        )}
        {/* List area - flex-1 ensures it takes available space even when empty */}
        <div className="flex flex-col gap-2 flex-1 min-h-0">
          {!isLoading && visibleItems.length === 0 && (
            <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              No items
            </div>
          )}
          {visibleItems.map((item, idx) => {
            const isClickable = getItemNavigationPath(item, dataSource) !== null;
            const baseClassName = `flex flex-row items-center gap-2 w-full min-w-0 flex-shrink-0 ${
              isClickable ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors rounded px-2 py-1.5 -mx-2' : ''
            }`;

            // If selectedColumns is provided, use those columns in order
            if (selectedColumns && selectedColumns.length > 0) {
              return (
                <div
                  key={idx}
                  className={baseClassName}
                  onClick={() => isClickable && handleItemClick(item)}
                >
                  {selectedColumns.map((columnKey, colIdx) => {
                    const value = item[columnKey as keyof typeof item];
                    let displayValue = Array.isArray(value) ? value.join(', ') : String(value || '');
                    let isTimestamp = false;
                    
                    // Format timestamp fields with relative time
                    if (columnKey === 'created_at' || columnKey === 'updated_at' || columnKey === 'last_updated') {
                      displayValue = formatRelativeTime(String(value));
                      isTimestamp = true;
                    }
                    
                    // For the first column (name), use flex-1 to take available space
                    // For timestamps in non-first position, show with "edited" prefix
                    const isFirstColumn = colIdx === 0;
                    const isLastTimestamp = isTimestamp && !isFirstColumn;
                    
                    return (
                      <React.Fragment key={columnKey}>
                        <span className={`${
                          isFirstColumn 
                            ? 'font-medium text-gray-900 dark:text-gray-100 truncate flex-1' 
                            : isLastTimestamp
                            ? 'text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap'
                            : 'text-gray-500 dark:text-gray-400 text-sm truncate max-w-[200px]'
                        }`}>
                          {isLastTimestamp ? `edited ${displayValue}` : displayValue}
                        </span>
                        {colIdx < selectedColumns.length - 1 && !isLastTimestamp && (
                          <span className="mx-1 flex items-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 inline-block" />
                          </span>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              );
            }
            
            // Default display (backward compatibility)
            return (
              <div
                key={idx}
                className={baseClassName}
                onClick={() => isClickable && handleItemClick(item)}
              >
                <span className="font-medium text-gray-900 dark:text-gray-100 truncate flex-1">
                  {item.name}
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap ml-2">
                  edited {formatRelativeTime(item.updated_at)}
                </span>
              </div>
            );
          })}
        </div>
        {/* "See more" button always at the bottom */}
        {hasMore && !showAll && (
          <button
            className="mt-2 text-xs text-blue-600 hover:underline self-start flex-shrink-0"
            onClick={handleSeeMoreClick}
          >
            see more...
          </button>
        )}
      </div>
    </CardBase>
  );
};

export default ListWidget;
